import { setInFilter } from 'utils'

import { ResultObjBaseWithReactObj } from './ext'
import type { FilterMap, GQLVariables, SortMap } from './types'
import { getOrderStrByName, getStrBycCamelName, removeSortKey } from './ngqlUtils'
import { getUIAdapter } from './state'

/**
 * ResultObj / MemTableObj 가 공유하는 페이지네이션·필터·정렬 상태 기반 클래스.
 * 두 클래스 모두 tableInfo 기반 UI 상태를 가지므로 공통 필드/메서드를 여기서 관리한다.
 */
export class PaginatedResultObjBase extends ResultObjBaseWithReactObj {
  mobileMode: boolean = false
  isParamChecked: boolean = false

  /** MemTableObj 는 이 플래그를 true 로 설정한다. 네트워크 refetch 유무 분기에 쓰인다. */
  isMemTable?: boolean

  filterMap?: FilterMap
  sortMap?: SortMap
  filterKeyList?: string[]
  sortKeyList?: string[]

  protected variables: GQLVariables = {}

  /** 서브클래스가 arrow field 로 구현한다. URL param 등을 variables 에 동기화. */
  checkParams?: () => unknown

  getDefaultVariable(): GQLVariables {
    let defaultVariables: unknown = null
    if (this.tableInfo != null) {
      if (this.mobileMode === true && this.tableInfo.mobileInfo != null) {
        defaultVariables = (this.tableInfo.mobileInfo as Record<string, unknown>).defaultVariables
      } else {
        defaultVariables = this.tableInfo.defaultVariables
      }
    }
    return (defaultVariables as GQLVariables) || {}
  }

  removeSortKeys(sorts: string[], sortMap?: SortMap): void {
    this.sortKeyList?.forEach((key) => {
      removeSortKey(key, sorts)
      if (sortMap != null) {
        delete sortMap[key]
      }
    })
  }

  /**
   * close 공통 정리: mobileModeOff 호출 + tableInfo.defaultFilters 삭제.
   * 각 서브클래스 close 에서 자기만의 정리(예: subscription.unsubscribe)를 한 뒤 호출한다.
   */
  protected cleanupMobileAndFilters(): void {
    if (this.reactObj != null && (this.reactObj as Record<string, unknown>).mobileModeOff != null) {
      ;(this.reactObj as Record<string, () => void>).mobileModeOff()
    }
    if (this.tableInfo != null && this.tableInfo.defaultFilters != null) {
      delete this.tableInfo.defaultFilters
    }
  }

  // ==========================================================================
  // Table/List 렌더링 공통 표면 (과거 top-level `makeArrayElemFunc` 가 동적 주입)
  // ==========================================================================

  getPageControl(): unknown {
    let pageCtrl: unknown = null
    if (this.tableInfo != null) {
      if (this.mobileMode === true && this.tableInfo.mobileInfo != null) {
        pageCtrl = (this.tableInfo.mobileInfo as Record<string, unknown>).pageControl
      }
      if (pageCtrl == null) {
        pageCtrl = this.tableInfo.pageControl
      }
    }
    return pageCtrl
  }

  getTableInfo(options?: unknown): unknown {
    const adapter = getUIAdapter()
    return this.renderArrayElement(
      options,
      this.buildTableColumn as (...args: unknown[]) => unknown,
      adapter.createTableInfo,
    )
  }

  getTableElem(options?: unknown): unknown {
    const adapter = getUIAdapter()
    return this.renderArrayElement(
      options,
      this.buildTableColumn as (...args: unknown[]) => unknown,
      adapter.createTableElem,
    )
  }

  getListInfo(options?: unknown): unknown {
    const adapter = getUIAdapter()
    return this.renderArrayElement(
      options,
      this.buildListItem as (...args: unknown[]) => unknown,
      adapter.createListInfo,
    )
  }

  getListElem(options?: unknown): unknown {
    const adapter = getUIAdapter()
    return this.renderArrayElement(
      options,
      this.buildListItem as (...args: unknown[]) => unknown,
      adapter.createListElem,
    )
  }

  getArrayElem(options?: unknown, type?: string): unknown {
    let curType: string | undefined = type
    if (curType == null && this.tableInfo != null) {
      if (this.mobileMode === true && this.tableInfo.mobileInfo != null) {
        curType = (this.tableInfo.mobileInfo as Record<string, unknown>).viewType as
          | string
          | undefined
      }
      if (curType == null) {
        curType = this.tableInfo.viewType as string | undefined
      }
      if (curType == null) {
        curType = 'table'
      }
    }

    if (curType != null && curType.toLowerCase() === 'list') {
      return this.getListElem(options)
    }
    return this.getTableElem(options)
  }

  private renderArrayElement(
    options: unknown,
    itemFunc: (...args: unknown[]) => unknown,
    retFunc: (...args: unknown[]) => unknown,
  ): unknown {
    return this.getElemData(
      options as Record<string, unknown> | null | undefined,
      this.tableInfo,
      itemFunc,
      retFunc,
    )
  }

  /**
   * 서브클래스가 dataSource 를 어떻게 결정할지 정의한다.
   * - `ResultObj`: 쿼리 결과의 `dataList[0].nodes` 를 사용
   * - `MemTableObj`: 인메모리 `itemList` 를 사용
   */
  protected getElemData(
    _options: Record<string, unknown> | null | undefined,
    _elemInfo: unknown,
    _itemFunc: (...args: unknown[]) => unknown,
    _retFunc: (...args: unknown[]) => unknown,
  ): unknown {
    throw new Error('getElemData must be overridden by subclass')
  }

  // ==========================================================================
  // Table column / list item 빌더 (과거 top-level `getColumn` / `getListItem`)
  // - filterMap / filterKeyList / sortKeyList 를 축적하고,
  // - 컬럼별 filteredValue / sortOrder 초기값을 tableInfo.defaultFilters/Sorts 와 병합한다.
  // ==========================================================================

  /**
   * options.i18nextKey 우선, 없으면 node.uiProps.i18nextKey 사용.
   * columns/list 공통의 auto-title 조회 키.
   */
  private resolveI18nextKey(node: unknown): string | null | undefined {
    const fromGqlopt = (this.gqlopt as Record<string, unknown> | undefined)?.i18nextKey as
      | string
      | null
      | undefined
    if (fromGqlopt != null) {
      return fromGqlopt
    }
    const uiProps = (node as { uiProps?: { i18nextKey?: string } } | null)?.uiProps
    return uiProps?.i18nextKey
  }

  protected buildTableColumn = (
    node: unknown,
    props: Record<string, unknown>,
    tableInfo: Record<string, unknown>,
  ): Record<string, unknown> => {
    type ColumnItem = {
      dataIndex?: string[]
      key?: string
      title?: unknown
      filters?: Array<{ value: unknown }>
      sorter?: unknown
      filteredValue?: unknown
      defaultFilteredValue?: unknown
      sortOrder?: unknown
      defaultSortOrder?: unknown
      [key: string]: unknown
    }
    const getColumnsFn = tableInfo.getColumns as (
      props: Record<string, unknown>,
      reactObj: unknown,
    ) => ColumnItem[]
    const columns: ColumnItem[] = getColumnsFn(props, this.reactObj)

    // 1) filterMap / filterKeyList / sortKeyList 초기 축적
    columns.forEach((item) => {
      if (item.dataIndex != null) {
        const itemKey = item.dataIndex.join('.')

        if (item.filters != null) {
          if (this.filterMap == null) {
            this.filterMap = {}
            this.filterKeyList = []
          }
          if ((this.filterMap as Record<string, unknown>)[itemKey] == null) {
            const filterValues: unknown[] = []
            item.filters.forEach((filterItem) => {
              filterValues.push(filterItem.value)
            })
            ;(this.filterMap as Record<string, unknown>)[itemKey] = filterValues
          }
          if (this.filterKeyList!.indexOf(itemKey) < 0) {
            this.filterKeyList!.push(itemKey)
          }
        }

        if (item.sorter != null && item.sorter !== false) {
          let sortKey = item.key
          if (sortKey == null && item.dataIndex != null) {
            sortKey = item.dataIndex[item.dataIndex.length - 1]
            sortKey = getStrBycCamelName(sortKey)
          }
          if (this.sortKeyList == null) {
            this.sortKeyList = []
          }
          if (sortKey != null && this.sortKeyList.indexOf(sortKey) < 0) {
            this.sortKeyList.push(sortKey)
          }
        }
      }
    })

    // 2) URL params 등 초기값 동기화
    this.checkParams?.()

    // 3) auto-title (i18n)
    const i18nextKey0 = this.resolveI18nextKey(node)
    if (i18nextKey0 != null) {
      const t = props.t as ((k: string) => string) | undefined
      columns.forEach((item) => {
        if (item.title == null && item.dataIndex != null && item.dataIndex.length === 2 && t) {
          /* i18next-extract-disable-next-line */
          item.title = t(`${i18nextKey0}.column.${item.dataIndex[1]}`)
        }
      })
    }

    // 4) 컬럼별 filteredValue / sortOrder 계산
    const isMem = this.isMemTable === true
    columns.forEach((item) => {
      if (item.dataIndex != null) {
        const itemKey = item.dataIndex.join('.')

        if (!isMem) {
          item.filteredValue =
            this.variables.filters != null
              ? (this.variables.filters as Record<string, unknown>)[itemKey]
              : null
        } else {
          item.filteredValue = item.filteredValue || item.defaultFilteredValue || null

          if (item.filteredValue != null && (item.filteredValue as unknown[]).length > 0) {
            // defaultVariables 에서는 sort/filter 외 필드의 초기값을 지정하고,
            // sort/filter 에서 처리되는 필드는 컬럼 정의에서 초기값을 지정한다.
            const filterKey = item.dataIndex[item.dataIndex.length - 1]
            if (this.variables.filters == null) {
              this.variables.filters = {}
            }
            setInFilter(
              this.variables.filters as Record<string, unknown>,
              filterKey,
              item.filteredValue as unknown[] | null | undefined,
            )
          }
        }

        if (tableInfo.defaultFilters != null) {
          const df = tableInfo.defaultFilters as Record<string, unknown>
          if (df[itemKey] != null) {
            if (!isMem) {
              item.filteredValue = df[itemKey]
            } else {
              item.defaultFilteredValue = df[itemKey]
            }
          }
        }
      }

      if (item.sorter != null && item.sorter !== false) {
        let sortKey = item.key
        if (sortKey == null && item.dataIndex != null) {
          sortKey = item.dataIndex[item.dataIndex.length - 1]
          sortKey = getStrBycCamelName(sortKey)
        }

        if (this.sortMap != null) {
          if (sortKey != null) {
            item.sortOrder = this.sortMap[sortKey]
          }
        } else {
          item.sortOrder = item.sortOrder || item.defaultSortOrder || null

          if (item.sortOrder != null && sortKey != null) {
            const sortEnum = getOrderStrByName(sortKey, item.sortOrder === 'ascend')
            if (this.variables.sort != null) {
              removeSortKey(sortKey, this.variables.sort as string[])
            } else {
              this.variables.sort = []
            }
            ;(this.variables.sort as string[]).push(sortEnum)
            if ((this.variables.sort as string[]).length === 0) {
              delete this.variables.sort
            }
          }
        }
        if (tableInfo.defaultSorts != null && sortKey != null) {
          const ds = tableInfo.defaultSorts as Record<string, unknown>
          if (ds[sortKey] != null) {
            const order = ds[sortKey] === true ? 'ascend' : 'descend'
            if (!isMem) {
              item.sortOrder = order
            } else {
              item.defaultSortOrder = order
            }
          }
        }
      }

      if (!isMem) {
        // memTable 이면 controlled parameter 를 모두 해제.
        item.filteredValue = item.filteredValue || null
        item.sortOrder = item.sortOrder || null
      } else {
        delete item.filteredValue
        delete item.sortOrder
      }
    })

    const ret: Record<string, unknown> = { columns }
    if (tableInfo.getRowSelection != null) {
      ret.rowSelection = (
        tableInfo.getRowSelection as (props: Record<string, unknown>, reactObj: unknown) => unknown
      )(props, this.reactObj)
    }
    return ret
  }

  protected buildListItem = (
    node: unknown,
    props: Record<string, unknown>,
    listInfo: Record<string, unknown>,
  ): Record<string, unknown> => {
    const listItem: Record<string, unknown> =
      listInfo.getListItem != null
        ? (
            listInfo.getListItem as (
              props: Record<string, unknown>,
              reactObj: unknown,
            ) => Record<string, unknown>
          )(props, this.reactObj)
        : {}

    if (listInfo.getColumns != null) {
      const columnInfo = this.buildTableColumn(node, props, listInfo)
      listItem.columns = columnInfo.columns
    }

    return listItem
  }
}
